// src/components/Login.tsx
import React from "react";
import { Typography, TextField, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import AuthPageWrapperComponent from "../../Components/AuthenticationComponents/AuthPageWrapperComponent";
import { NavLink, useNavigate } from "react-router-dom";
import { login } from "../../reducers/auth_reducer";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { login as loginService } from "../../services/auth_service";
import { AppLogo } from "../../Helpers/constants";

const FormContainer = styled('div')(({ theme }) => ({
  height: "400px",
  width: "100%",
  borderRadius: "20px",
  backgroundColor: "transparent",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(2),
  alignItems: "center",
  justifyContent: "flex-start",
}));


const StyledForm = styled(Form)(({ theme }) => ({
  width: "100%",
  marginTop: theme.spacing(1),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
  backgroundColor: "#4D67EB",
  "&:hover": {
    backgroundColor: "#3755a3",
  },
}));

const StyledErrorMessage = styled('div')({
  color: "red",
  fontSize: "12px",
});

const StyledNavLink = styled(NavLink)({
  color: "#4D67EB",
  cursor: "pointer",
  textDecoration: "none",
});

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Required"),
  password: Yup.string().required("Required"),
});

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = async (values: any) => {
    try {
      const response = await loginService(values.email, values.password);
      if (response?.status?.status !== "error") {
        dispatch(
          login({
            token: response.jwt,
            user: { full_name: response.full_name },
          })
        );
        localStorage.setItem("token", response.jwt);
        navigate("/events");
      } else {
        Swal.fire({
          title: "Error",
          text: response.status.message,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "Invalid credentials",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <AuthPageWrapperComponent withBack={false}>
      {/* AppLogo at the top */}
      <div style={{ width: "100%", textAlign: "center", marginTop: "-20px" }}>
        <AppLogo width={"100%"} />
      </div>
      <FormContainer>
        {/* Title */}
        <Typography component="h1" variant="h4" sx={{
          width: "100%",
          textAlign: "center",
          marginBottom: 1,
          fontSize: "2rem",
          fontWeight: "bold",
          letterSpacing: "1px",
        }}>
          Welcome to eTicketing
        </Typography>
        {/* Subtitle */}
        <Typography component="h2" variant="subtitle1" sx={{
          marginBottom: 3,
          fontSize: "1.2rem",
          color: "#555",
          textAlign: "center",
        }}>
          Login to your account
        </Typography>
        <Formik
          initialValues={{ email: "", password: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched }) => (
            <StyledForm>
              <Field
                as={TextField}
                variant="outlined"
                margin="normal"
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                error={errors.email && touched.email ? true : false}
                autoFocus
              />
              <ErrorMessage name="email" component={StyledErrorMessage} />
              <Field
                as={TextField}
                variant="outlined"
                margin="normal"
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                error={errors.password && touched.password ? true : false}
              />
              <ErrorMessage name="password" component={StyledErrorMessage} />
              <Typography align="right">
                <StyledNavLink to="/forgot_password">
                  Forgot Password?
                </StyledNavLink>
              </Typography>
              <StyledButton
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={isSubmitting}
              >
                Sign In
              </StyledButton>
            </StyledForm>
          )}
        </Formik>
      </FormContainer>
    </AuthPageWrapperComponent>
  );
};

export default LoginPage;
